import { configFile } from "@/config";
import { TRatingItems } from "@/Types/types";
import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Input, Radio, RadioGroup, Spinner, Text, Textarea, useToast } from "@chakra-ui/react";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import axios from "axios";
import UserRatings from "./UserRatings";

export default function YourOpinion({ ratingData, breedId, breedRating }: any) {
  const [ratingItems, setRatingItems] = useState<TRatingItems[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [alreadyHadTheBreedError, setAlreadyHadTheBreedError] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const [ratingError, setRatingError] = useState<{ [key: string]: boolean }>({
    nameError: false,
    emailError: false,
    alreadyHadTheBreedError: false,
    descriptionError: false,
    ratingError: false
  });
  const [isMobileScreen, setIsMobileScreen] = useState<Boolean>();
  const [boxWidths, setBoxWidths] = useState<{ [key: string]: string }>({
    liveInApartment: "0%",
    attachedToTheOwner: "0%",
    barkTendency: "0%",
    easeToTrain: "0%",
    socializingWithChildren: "0%",
    coexistenceWithOtherAnimals: "0%",
    hairLoss: "0%",
  });
  const toast = useToast();
  const NUMBER_RADIO: number = 5
  const boxRef = useRef(null);

  const categoryMapping: { [key: string]: string } = {
    "Vive em apartamento": "liveInApartment",
    "Apego com o dono": "attachedToTheOwner",
    "Tendência de latir": "barkTendency",
    "Facilidade para adestrar": "easeToTrain",
    "Convívio com crianças": "socializingWithChildren",
    "Convívio com outros animais": "coexistenceWithOtherAnimals",
    "Queda de pelos": "hairLoss",
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      alreadyHadTheBreed: "",
      description: "",
      rating: {
        liveInApartment: "",
        socializingWithChildren: "",
        easeToTrain: "",
        attachedToTheOwner: "",
        barkTendency: "",
        hairLoss: "",
        coexistenceWithOtherAnimals: "",
      },
      breed: breedId,
    },
    onSubmit: async (values, { resetForm }) => {
      const dataToSend = {
        name: values.name,
        email: values.email,
        alreadyHadTheBreed: values.alreadyHadTheBreed === "true",
        description: values.description,
        rating: {
          liveInApartment: parseInt(values.rating.liveInApartment),
          socializingWithChildren: parseInt(values.rating.socializingWithChildren),
          attachedToTheOwner: parseInt(values.rating.attachedToTheOwner),
          coexistenceWithOtherAnimals: parseInt(values.rating.coexistenceWithOtherAnimals),
          barkTendency: parseInt(values.rating.barkTendency),
          hairLoss: parseInt(values.rating.hairLoss),
          easeToTrain: parseInt(values.rating.easeToTrain)
        },
        breed: parseInt(values.breed),
      }
      handleSubmit(dataToSend);
    }
  });

  const handleSubmit = async (data: any) => {
    setIsLoading(true);

    const namePattern = /^[A-Za-z\s]+$/;
    if (!namePattern.test(formik.values.name)) {
      setNameError(true);
    }

    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    if (!emailPattern.test(formik.values.email)) {
      setEmailError(true);
    }

    setAlreadyHadTheBreedError(formik.values.alreadyHadTheBreed === "");

    if (formik.values.description.length < 10) {
      setDescriptionError(true);
    }

    setRatingError(prevRatingError => ({
      ...prevRatingError,
      liveInApartment: formik.values.rating.liveInApartment === "",
      socializingWithChildren: formik.values.rating.socializingWithChildren === "",
      attachedToTheOwner: formik.values.rating.attachedToTheOwner === "",
      coexistenceWithOtherAnimals: formik.values.rating.coexistenceWithOtherAnimals === "",
      barkTendency: formik.values.rating.barkTendency === "",
      hairLoss: formik.values.rating.hairLoss === "",
      easeToTrain: formik.values.rating.easeToTrain === ""
    }));

    if (
      formik.values.name != ""
      && formik.values.email != ""
      && formik.values.alreadyHadTheBreed != ""
      && formik.values.description != ""
      && formik.values.rating.liveInApartment != ""
      && formik.values.rating.socializingWithChildren != ""
      && formik.values.rating.attachedToTheOwner != ""
      && formik.values.rating.coexistenceWithOtherAnimals != ""
      && formik.values.rating.barkTendency != ""
      && formik.values.rating.hairLoss != ""
      && formik.values.rating.easeToTrain != ""
    ) {
      try {
        await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/breed-ratings`, {
          data: data
        });
        toast({
          title: "Obrigado!",
          description: "Sua avaliação foi enviada.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        toast({
          title: "Erro!",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      setIsLoading(false);
    }
  }

  const handleChangeBoxWidth = (category: string, value: string) => {
    setBoxWidths({
      ...boxWidths,
      [category]: value,
    });
  };

  useEffect(() => {
    setIsMobileScreen(window.innerWidth <= 830 ? true : false);
  })

  useEffect(() => {
    setRatingItems([
      {
        icon: "/images/rating/icon1.svg",
        name: "Vive em apartamento",
        value: ratingData.liveInApartment
      },
      {
        icon: "/images/rating/icon2.svg",
        name: "Apego com o dono",
        value: ratingData.attachedToTheOwner
      },
      {
        icon: "/images/rating/icon3.svg",
        name: "Tendência de latir",
        value: ratingData.barkTendency
      },
      {
        icon: "/images/rating/icon4.svg",
        name: "Facilidade para adestrar",
        value: ratingData.easeToTrain
      },
      {
        icon: "/images/rating/icon5.svg",
        name: "Convívio com crianças",
        value: ratingData.socializingWithChildren
      },
      {
        icon: "/images/rating/icon6.svg",
        name: "Convívio com outros animais",
        value: ratingData.coexistenceWithOtherAnimals
      },
      {
        icon: "/images/rating/icon7.svg",
        name: "Queda de pelos",
        value: ratingData.hairLoss
      },
    ])
  }, [ratingData])

  const handleTrueOrFalse = (e: any) => {
    formik.setFieldValue("alreadyHadTheBreed", e);
  }

  return (
    <Flex
      direction="column"
      bgColor="#FFF"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
      borderRadius="16px"
      id="your-opinion"
    >
      <Flex
        p={{ base: "16px 16px 0 16px", lg: "32px 32px 0 32px" }}
        direction="column"
        gap="12px"
      >
        <Text
          fontSize={{ base: "24px", lg: "36px" }}
          lineHeight={{ base: "32px", lg: "40px" }}
          fontWeight="700"
          color="gray.800"
        >
          Deixe sua opinião sobre essa raça
        </Text>
        <Text
          fontSize={{ base: "16px", lg: "18px" }}
          lineHeight={{ base: "24px", lg: "28px" }}
          color="gray.500"
        >
          Diga-nos a sua opinião sobre essa raça
        </Text>
      </Flex>
      <form
        onSubmit={formik.handleSubmit}
        style={{ padding: isMobileScreen ? "16px" : "32px" }}
      >
        <FormLabel fontSize="18px" lineHeight="28px" fontWeight="700">
          Suas informações
        </FormLabel>
        <Flex gap={{ base: "16px", lg: "32px" }} direction={{ base: "column", lg: "row" }}>
          <FormControl isInvalid={nameError}>
            <Input
              type="text"
              size="lg"
              placeholder="Seu nome"
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
            />
            {nameError && (<FormErrorMessage>Insira um nome válido, sem caracteres especiais.</FormErrorMessage>)}
          </FormControl>
          <FormControl isInvalid={emailError}>
            <Input
              type="email"
              size="lg"
              placeholder="Seu e-mail"
              name="email"
              onChange={formik.handleChange}
              value={formik.values.email}
            />
            {emailError && (
              <FormErrorMessage>E-mail é obrigatório.</FormErrorMessage>
            )}
          </FormControl>
        </Flex>
        <Flex justifyContent={{ base: "normal", lg: "space-between" }} mt="32px" direction={{ base: "column", lg: "row" }}>
          <RadioGroup
            name="alreadyHadTheBreed"
            value={formik.values.alreadyHadTheBreed}
            onChange={handleTrueOrFalse}
          >
            <FormLabel fontSize="18px" lineHeight="28px" fontWeight="700">
              Você tem ou já teve algum pet desta raça?
            </FormLabel>
            <FormControl isInvalid={alreadyHadTheBreedError}>
              <Radio
                colorScheme="purple"
                size="lg"
                value="true"
              >
                Sim
              </Radio>
              <Radio
                colorScheme="purple"
                size="lg"
                ml="24px"
                value="false"
              >
                Não
              </Radio>
              {alreadyHadTheBreedError && (<FormErrorMessage>Escolha uma opção.</FormErrorMessage>)}
            </FormControl>
          </RadioGroup>
          <Flex direction="column" width={{ base: "100%", lg: "350px" }} mt={{ base: "32px", lg: "0" }}>
            <FormLabel fontSize="18px" lineHeight="28px" fontWeight="700">
              Faça uma breve descrição sobre a raça
            </FormLabel>
            <FormControl isInvalid={descriptionError}>
              <Textarea
                size="md"
                placeholder="Escreva aqui"
                name="description"
                onChange={formik.handleChange}
                value={formik.values.description}
              />
              {descriptionError && (
                <FormErrorMessage>Descrição é obrigatório.</FormErrorMessage>
              )}
            </FormControl>
          </Flex>
        </Flex>
        <Flex direction="column" mt="32px" gap="24px">
          <FormLabel fontSize="18px" lineHeight="28px" fontWeight="700">
            Avalie o comportamento desta raça
          </FormLabel>
          <Flex flexWrap="wrap" justifyContent="space-between" rowGap={{ base: "32px", lg: "48px" }}>
            {ratingItems.map((item: TRatingItems, index: number) => (
              <React.Fragment key={index}>
                <FormControl isInvalid={ratingError[categoryMapping[item.name]]} width={{ base: "100%", lg: "auto" }}>
                  <RadioGroup onChange={formik.handleChange}>
                    <Flex gap="8px">
                      <Image
                        src={item.icon}
                        alt="icon"
                        width="24"
                        height="24"
                      />
                      <Text
                        fontSize="18px"
                        lineHeight="28px"
                        color="gray.500"
                      >
                        {item.name}
                      </Text>
                    </Flex>
                    <Flex gap={{ base: "0", lg: "58px" }} mt="16px" position="relative" justifyContent={{ base: "space-between", lg: "normal" }}>
                      {ratingItems.slice(0, NUMBER_RADIO).map((_, i: number) => (
                        <Radio
                          key={i}
                          size="lg"
                          colorScheme="purple"
                          bgColor="#FFF"
                          position="relative"
                          zIndex="3"
                          value={`${i + 1}`}
                          name={`rating[${categoryMapping[item.name]}]`}
                          onChange={() => {
                            const newValue = `${i + 1}`;
                            handleChangeBoxWidth(categoryMapping[item.name], `${i * 25}%`);
                            formik.setFieldValue(`rating[${categoryMapping[item.name]}]`, newValue);
                          }}
                        />
                      ))}
                      <Box
                        height="10px"
                        width={boxWidths[categoryMapping[item.name]]}
                        bgColor="purple.200"
                        borderRadius="30px"
                        position="absolute"
                        zIndex="2"
                        top="50%"
                        bottom="50%"
                        transform="translateY(-50%)"
                        ref={boxRef}
                      />
                      <Box
                        height="10px"
                        width="100%"
                        bgColor="gray.200"
                        borderRadius="30px"
                        position="absolute"
                        zIndex="1"
                        top="50%"
                        bottom="50%"
                        transform="translateY(-50%)"
                      />
                    </Flex>
                  </RadioGroup>
                  {ratingError && (
                    <FormErrorMessage>Escolha uma nota de 1 a 5</FormErrorMessage>
                  )}
                </FormControl>
              </React.Fragment>
            ))}
          </Flex>
        </Flex>
        <Button aria-label="Publicar avaliação" size="lg" width="100%" color="#FFF" bgColor={configFile.primaryAccentColor} _hover={{ bgColor: configFile.secondaryAccentColor }}
          type="submit" mt="32px" borderRadius="54px"
        >
          Publicar avaliação
        </Button>
        {isLoading && (
          <Flex width="100%">
            <Spinner
              margin="0 auto"
              mt="15px"
              color={configFile.primaryAccentColor}
            />
          </Flex>
        )}
      </form>
      <UserRatings breedRating={breedRating} />
    </Flex >
  )
}
